import React from 'react';
import { useIntl } from 'react-intl';

import { RatingsInsight, RatingsTotal } from 'apis/merchant-gateway/insights';

import SummaryTable from './SummaryTable';
import { useInsightSubRoutes } from '../../hooks/useInsightsSubRoutes';
import { InsightResponseWithPreviousInterval, JsonPath, MetricRow, RowType } from '../../types';
import { calculateGrowthRate, formatGrowthRate, getGrowthTrend } from '../../utils';
import { NbspAroundNumber } from '../NbspAroundNumber';

const RatingsAndReviews = ({
  ratingsInsight,
}: {
  ratingsInsight: InsightResponseWithPreviousInterval<RatingsInsight>;
}) => {
  const { formatMessage, formatNumber, locale } = useIntl();
  const { ratingsAndReviewsLink } = useInsightSubRoutes();

  const selectedTotal = ratingsInsight.selected.data?.data.ratings?.total;
  const previousTotal = ratingsInsight.previous.data?.data.ratings?.total;

  const rowsConfig: {
    title: JsonPath;
    titleTooltip: JsonPath;
    getSubtitle?: (total: RatingsTotal) => React.ReactNode;
    getValue: (total: RatingsTotal) => number;
    invertTrend?: boolean;
  }[] = [
    {
      title: 'insights.ratings-and-reviews.rating',
      titleTooltip: 'insights.rating.tooltip',
      getSubtitle: ({ lifetimeRating: rating }) => {
        return rating == null ? null : (
          <NbspAroundNumber
            message={formatMessage({ id: 'insights.ratings-and-reviews.shown-in-wolt' }, { rating })}
            numericValue={rating}
          />
        );
      },
      getValue: total => total.averageRating,
    },
    {
      title: 'insights.ratings-and-reviews.total-reviews',
      titleTooltip: 'insights.total-reviews.tooltip',
      getValue: total => total.totalRatings,
    },
    {
      title: 'insights.ratings-and-reviews.rated-positive',
      titleTooltip: 'insights.rated-positive.tooltip',
      getValue: total => total.positiveRatings,
    },
    {
      title: 'insights.ratings-and-reviews.rated-negative',
      titleTooltip: 'insights.rated-negative.tooltip',
      getValue: total => total.negativeRatings,
      invertTrend: true,
    },
  ];

  const rows = rowsConfig.map(({ getValue, getSubtitle, invertTrend, ...row }): MetricRow => {
    const result: MetricRow = {
      subtitle: selectedTotal == null ? null : getSubtitle?.(selectedTotal),
      type: RowType.Metric as const,
      metrics: 0,
      growthRate: '',
      growthTrend: 'neutral',
      ...row,
    };

    if (selectedTotal != null) {
      const metrics = getValue(selectedTotal);
      result.metrics = formatNumber(metrics, { maximumFractionDigits: 1 });
      if (previousTotal != null) {
        const previousMetrics = getValue(previousTotal);
        result.growthRate = formatGrowthRate(calculateGrowthRate(previousMetrics, metrics), locale);
        result.growthTrend = getGrowthTrend(calculateGrowthRate(previousMetrics, metrics), invertTrend);
      }
    }

    return result;
  });

  return (
    <SummaryTable
      isError={ratingsInsight.isError}
      isFetching={ratingsInsight.isFetching}
      onRetry={ratingsInsight.refetch}
      title={formatMessage({ id: 'insights.ratings-and-reviews.header' })}
      tooltip={formatMessage({ id: 'insights.ratings-and-reviews.tooltip-scale-10' })}
      rows={rows}
      link={ratingsAndReviewsLink}
    />
  );
};

export default RatingsAndReviews;
